<script setup lang="ts">
import { computed } from "vue";
import { UiCard, UiChip, UiIcon, UiText } from "@shopexperts/shared";
import SatelliteSiteActionButton from "./SatelliteSiteActionButton.vue";
import type { SatelliteContactSite, SatelliteContactSiteStats } from "@shopexperts/legacy-api";
import {
    formatSatelliteDate,
    formatSubmissionCount,
    formatStatLabel,
    STAT_KEYS,
} from "@/app/composables/satellite-contact-sites/satelliteSiteFormatters";

const props = defineProps<{
    site: SatelliteContactSite;
    stats?: SatelliteContactSiteStats | null;
}>();

const emit = defineEmits<{
    (e: "edit"): void;
    (e: "delete"): void;
    (e: "regenerate-key"): void;
    (e: "view"): void;
}>();

const hostname = computed(() => {
    try {
        return new URL(props.site.website_url).hostname;
    } catch {
        return props.site.website_url;
    }
});
</script>

<template>
    <UiCard padding="md" class="hover:shadow-md transition-shadow">
        <div class="flex flex-col gap-4">
            <div class="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
                <div class="flex min-w-0 flex-1 flex-col gap-2">
                    <div class="flex flex-wrap items-center gap-2">
                        <UiText as="p" variant="h6" class="font-semibold text-se-dark truncate">
                            {{ site.name }}
                        </UiText>
                        <UiChip
                            :text="site.is_active ? 'Active' : 'Inactive'"
                            :variant="site.is_active ? 'success' : 'neutral'"
                            size="sm"
                        />
                    </div>

                    <div class="flex flex-wrap items-center gap-2 text-se-gray-600">
                        <UiIcon name="link-external" class="h-4 w-4 shrink-0" />
                        <a
                            :href="site.website_url"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="truncate text-sm text-se-dark underline"
                        >
                            {{ hostname }}
                        </a>
                    </div>
                </div>

                <div class="shrink-0 min-w-[140px]">
                    <UiText as="p" variant="caption-normal" class="text-se-gray-500 uppercase tracking-wider text-xs" text="Submissions" />
                    <UiText as="p" variant="p" class="font-medium text-se-dark" :text="formatSubmissionCount(site.submissions_count)" />
                </div>

                <div class="shrink-0 min-w-[180px]">
                    <UiText as="p" variant="caption-normal" class="text-se-gray-500 uppercase tracking-wider text-xs" text="Last used" />
                    <UiText as="p" variant="p" class="font-medium text-se-dark" :text="formatSatelliteDate(site.last_used_at)" />
                </div>

                <div class="flex flex-wrap items-center gap-x-3 gap-y-2 sm:gap-x-4 shrink-0">
                    <SatelliteSiteActionButton
                        label="Details"
                        icon="preview"
                        tone="primary"
                        @click="emit('view')"
                    />
                    <SatelliteSiteActionButton
                        label="Edit"
                        icon="pencil"
                        @click="emit('edit')"
                    />
                    <SatelliteSiteActionButton
                        label="Regenerate key"
                        icon="repeat"
                        tone="warn"
                        @click="emit('regenerate-key')"
                    />
                    <SatelliteSiteActionButton
                        label="Delete"
                        icon="delete"
                        tone="danger"
                        @click="emit('delete')"
                    />
                </div>
            </div>

            <div v-if="stats" class="grid grid-cols-2 gap-3 border-t border-se-gray-100 pt-4 lg:grid-cols-4">
                <div
                    v-for="key in STAT_KEYS"
                    :key="key"
                    class="rounded-md bg-se-gray-50 px-3 py-2"
                >
                    <UiText
                        as="p"
                        variant="caption-normal"
                        class="text-se-gray-500 uppercase tracking-wider text-xs"
                        :text="formatStatLabel(key)"
                    />
                    <UiText
                        as="p"
                        variant="p-small"
                        class="mt-1 font-semibold tabular-nums text-se-dark"
                        :text="String(stats[key])"
                    />
                </div>
            </div>
        </div>
    </UiCard>
</template>
